// types
import { Control } from 'react-hook-form';
import { RecurrencePattern } from './components/RecurrencePatternBuilder/RecurrencePatternBuilder.types';
import {
  ActivityResponse,
  AppointmentExceptionResponse,
  AppointmentType,
  DerivedTreatmentResponse,
  PlanningEntityIdentifier,
  RecurrenceExceptionUpdateType,
  SchedulingLabel
} from 'generated/index';

export type BaseFormSectionProps = {
  appointment: AppointmentFormFieldData;
  control: Control<any>;
};

export type AppointmentFormFieldData = {
  id?: string;
  activity?: ActivityResponse | null;
  appointmentExceptions?: AppointmentExceptionResponse[];
  isCopyPasteAction?: boolean;
  appointmentType?: AppointmentType;
  notePublic?: string;
  noteInternal?: string;
  start?: string;
  originalOccurrenceStart?: string;
  isRecurring?: boolean;
  end?: string;
  recurrenceExceptionUpdateType?: RecurrenceExceptionUpdateType;
  recurrencePattern?: RecurrencePattern;
  allowedToEditRecurrenceRule?: boolean;
  planEntities: AppointmentFormFieldEntities;
  /**
   * What the appointment belongs to, carried so a re-save keeps it. The payload rebuilds the appointment
   * from this object, so anything absent here is dropped from the appointment — and these three are set
   * elsewhere (imports, afsprakenplanning's own flows) rather than by this form, which is exactly why they
   * have to survive a round trip the form did not originate.
   */
  organizationalUnitId?: number;
  projectEntityId?: string;
  appointmentGroupingEntityId?: string;
  schedulingLabel?: SchedulingLabel;
  editAppointmentType?: AppointmentRecurrenceType | null;
  /**
   * Display only: the treatment each care receiver is derived to be on for this occurrence. It
   * arrives on the fetched appointment, so it appears here, but the store lifts it out into
   * `original` before the form ever sees it — it is read-only context the planner cannot edit,
   * not form state. Do not add it to AppointmentForm.schema.ts.
   *
   * It could not reach a request body in any case: every save goes through
   * `convertFormFieldDataToPayload`, which builds the payload field by field and never spreads the
   * form data. So this is about keeping form state to what the form owns, not about stripping.
   */
  derivedTreatments?: DerivedTreatmentResponse[];
};

export type AppointmentFormFieldEntities = {
  EMPLOYEE: AppointmentFormFieldEntity[];
  CLIENT: AppointmentFormFieldEntity[];
  WORKPLACE: AppointmentFormFieldEntity[];
  RESOURCE: AppointmentFormFieldEntity[];
};

export type AppointmentFormFieldEntity = PlanningEntityIdentifier & {
  label: string;
};

export enum AppointmentRecurrenceType {
  ONLY_THIS = 'ONLY_THIS',
  THIS_AND_ALL_SUBSEQUENT = 'THIS_AND_ALL_SUBSEQUENT'
}
